import React, { useEffect, useState, useCallback } from 'react';
import { Send, Filter, CheckSquare, MessageSquare, Smartphone, AlertCircle, Users, XCircle, CheckCircle2, ClipboardList } from 'lucide-react';
import ServiceContainer from '../../../core/services/ServiceContainer';
import { useGroupStore } from '../../../core/services/GroupStore';
import { useDamStore } from '../../../core/services/DamStore';
import { useMessageTemplateStore } from '../../../core/services/MessageTemplateStore';
import { IMessage, IQueueJob, IQueueJobResultGroup, MessageType } from '../../../core/interfaces/IMessage';
import { Card, CardContent, CardHeader, CardTitle } from '../../../shared/components/ui/card';
import { Button } from '../../../shared/components/ui/button';
import { Label } from '../../../shared/components/ui/label';
import { Badge } from '../../../shared/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../shared/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../shared/components/ui/dialog';

const serviceContainer = ServiceContainer.getInstance();
const groupService = serviceContainer.getGroupService();
const damService = serviceContainer.getDamService();
const messageService = serviceContainer.getMessageService();
const templateService = serviceContainer.getMessageTemplateService();

export const SendMessagesPage: React.FC = () => {
  const { groups, setGroups, isLoading: groupsLoading, setLoading: setGroupsLoading, setError: setGroupsError } = useGroupStore();
  const { dams, setDams } = useDamStore();
  const { templates, setTemplates } = useMessageTemplateStore();

  const [selectedDamId, setSelectedDamId] = useState<string>('');
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [messageType, setMessageType] = useState<MessageType>(MessageType.SMS);
  const [messageBody, setMessageBody] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  interface SendLogEntry {
    groupId: string;
    groupSigla: string;
    groupDamName: string;
    result: IMessage | null;
    error: string | null;
  }

  const [sending, setSending] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sendLog, setSendLog] = useState<SendLogEntry[]>([]);
  const [queueJob, setQueueJob] = useState<IQueueJob | null>(null);
  const [isQueueModalOpen, setIsQueueModalOpen] = useState(false);

  const loadData = useCallback(async () => {
    try {
      if (!groups.length) {
        setGroupsLoading(true);
      }
      const [groupsData, damsData, templatesData] = await Promise.all([
        groupService.getAllGroups(),
        damService.getAllDams(),
        templateService.getAllTemplates(),
      ]);
      setGroups(groupsData);
      setDams(damsData);
      setTemplates(templatesData);
    } catch (err) {
      setGroupsError('Erro ao carregar dados');
      console.error(err);
    } finally {
      setGroupsLoading(false);
    }
  }, [groups.length, setGroups, setDams, setTemplates, setGroupsLoading, setGroupsError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (templateId) {
      const template = templates.find((t) => t.id === templateId);
      if (template) {
        setMessageBody(template.body);
      }
    } else {
      setMessageBody('');
    }
  };

  const handleGroupToggle = (groupId: string) => {
    setSelectedGroups((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    );
  };

  const handleSelectAllGroups = () => {
    const filteredGroups = selectedDamId
      ? groups.filter((g) => g.damId === selectedDamId)
      : groups;
    
    const allSelected = filteredGroups.every((g) => selectedGroups.includes(g.id));
    
    if (allSelected) {
      setSelectedGroups((prev) => prev.filter((id) => !filteredGroups.find((g) => g.id === id)));
    } else {
      const newSelected = [...selectedGroups];
      filteredGroups.forEach((g) => {
        if (!newSelected.includes(g.id)) {
          newSelected.push(g.id);
        }
      });
      setSelectedGroups(newSelected);
    }
  };

  const handleSendMessages = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedGroups.length === 0) {
      setErrorMessage('Selecione pelo menos um grupo');
      return;
    }

    if (!selectedTemplateId) {
      setErrorMessage('Selecione um template para enviar mensagens');
      return;
    }

    if (!messageBody.trim()) {
      setErrorMessage('Digite uma mensagem');
      return;
    }

    const selectedTemplate = templates.find((template) => template.id === selectedTemplateId);

    if (messageType === MessageType.WHATSAPP && !selectedTemplate?.sidTemplate) {
      setErrorMessage('Template sem SID da Twilio. Sincronize o template antes de enviar no WhatsApp.');
      return;
    }

    setSending(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    setSendLog([]);
    setQueueJob(null);
    setIsQueueModalOpen(true);

    try {
      const queued = await messageService.sendGroupsQueued({
        groupIds: selectedGroups,
        body: messageBody,
        type: messageType,
        sidTemplate: messageType === MessageType.WHATSAPP ? selectedTemplate?.sidTemplate : undefined,
      });

      setQueueJob(queued);
      setSuccessMessage('Envio em fila iniciado. Acompanhe o progresso no pop-up.');
    } catch (err) {
      setIsQueueModalOpen(false);
      setErrorMessage('Erro ao enviar mensagens. Tente novamente.');
      console.error(err);
    }
  };

  useEffect(() => {
    const queueJobId = queueJob?.id || queueJob?.jobId;
    if (!queueJobId || !queueJob) {
      return;
    }

    if (queueJob.status === 'completed' || queueJob.status === 'failed') {
      return;
    }

    const intervalId = window.setInterval(async () => {
      try {
        const latest = await messageService.getQueueJob(queueJobId);
        setQueueJob(latest);

        if (latest.status === 'completed' || latest.status === 'failed') {
          setSending(false);

          if (latest.status === 'failed') {
            setErrorMessage(latest.error || 'Falha ao processar fila de envio.');
            return;
          }

          const groupResults = latest.result?.groupResults || [];
          const logEntries: SendLogEntry[] = groupResults.map((entry: IQueueJobResultGroup) => {
            const group = groups.find((g) => g.id === entry.groupId);
            return {
              groupId: entry.groupId,
              groupSigla: group?.sigla || entry.groupId,
              groupDamName: group?.damName || '',
              result: entry.response || null,
              error: entry.status === 'failed' ? entry.error || 'Erro desconhecido' : null,
            };
          });
          setSendLog(logEntries);

          const successful = latest.result?.successItems ?? 0;
          const failed = latest.result?.failedItems ?? 0;

          if (failed === 0) {
            setSuccessMessage(`Mensagens enviadas com sucesso para ${successful} grupo(s)!`);
            setMessageBody('');
            setSelectedTemplateId('');
            setSelectedGroups([]);
          } else {
            setSuccessMessage(`${successful} grupo(s) enviado(s), ${failed} falhou(ram).`);
          }

          window.dispatchEvent(new CustomEvent('snm:messages-sent'));
        }
      } catch (error) {
        setSending(false);
        setErrorMessage('Não foi possível consultar o progresso da fila.');
        console.error(error);
      }
    }, 1200);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [queueJob, groups]);

  useEffect(() => {
    if (!isQueueModalOpen || !queueJob) {
      return;
    }

    const isFinishedWithoutErrors =
      queueJob.status === 'completed' && (queueJob.failedItems || 0) === 0;

    if (!isFinishedWithoutErrors) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsQueueModalOpen(false);
    }, 15000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isQueueModalOpen, queueJob]);

  const filteredGroups = selectedDamId
    ? groups.filter((g) => g.damId === selectedDamId)
    : groups;

  const selectedGroupsWithData = groups.filter((g) => selectedGroups.includes(g.id));
  const totalRecipients = selectedGroupsWithData.reduce(
    (sum, group) => sum + (group.memberCount || 0),
    0
  );
  const queueIsRunning = queueJob?.status === 'queued' || queueJob?.status === 'running';
  const queueHasFailed = queueJob?.status === 'failed';
  const queueFinishedWithoutErrors = queueJob?.status === 'completed' && (queueJob?.failedItems || 0) === 0;
  const queueFinishedWithWarnings = queueJob?.status === 'completed' && (queueJob?.failedItems || 0) > 0;
  const queueStatusLabel = queueJob?.status === 'queued'
    ? 'Na fila'
    : queueJob?.status === 'running'
    ? 'Processando'
    : queueJob?.status === 'completed'
    ? 'Concluído'
    : 'Falhou';
  const queueBarColor = queueHasFailed
    ? 'bg-red-500'
    : queueFinishedWithoutErrors
    ? 'bg-emerald-500'
    : 'bg-amber-500';

  if (groupsLoading && !groups.length) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Carregando dados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Send className="h-8 w-8 text-blue-600" />
            Enviar Mensagens
          </h1>
          <p className="text-slate-600 mt-2">
            <Badge variant="secondary" className="mr-2">{selectedGroups.length} grupo(s)</Badge>
            <Badge variant="default">{totalRecipients} destinatário(s)</Badge>
          </p>
        </div>
      </div>

      {errorMessage && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="h-4 w-4" />
            <span>{errorMessage}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setErrorMessage(null)}>✕</Button>
        </div>
      )}

      {successMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2 text-green-600">
            <CheckSquare className="h-4 w-4" />
            <span>{successMessage}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setSuccessMessage(null)}>✕</Button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-5 w-5 text-blue-600" />
              Selecionar Grupos
            </CardTitle>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="default" className="bg-blue-600">
                {selectedGroups.length} selecionado(s)
              </Badge>
              <span className="text-sm text-slate-500">de {filteredGroups.length} disponível(is)</span>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="damFilter" className="flex items-center gap-2 text-sm font-medium">
                <Filter className="h-4 w-4 text-slate-600" />
                Filtrar por Barragem
              </Label>
              <div className="flex gap-2">
                <Select value={selectedDamId} onValueChange={setSelectedDamId}>
                  <SelectTrigger id="damFilter" className="flex-1">
                    <SelectValue placeholder="Todas as barragens" />
                  </SelectTrigger>
                  <SelectContent>
                    {dams.map((dam) => (
                      <SelectItem key={dam.id} value={dam.id}>
                        {dam.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedDamId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedDamId('')}
                    className="px-3"
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant={filteredGroups.every((g) => selectedGroups.includes(g.id)) && selectedGroups.length > 0 ? "default" : "outline"}
                size="sm"
                onClick={handleSelectAllGroups}
                className="flex-1"
              >
                <CheckSquare className="h-4 w-4 mr-2" />
                {filteredGroups.every((g) => selectedGroups.includes(g.id)) && selectedGroups.length > 0 ? 'Desmarcar Todos' : 'Selecionar Todos'}
              </Button>
              {selectedGroups.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedGroups([])}
                  className="px-3 text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <XCircle className="h-4 w-4" />
                </Button>
              )}
            </div>

            <div className="space-y-2 max-h-[480px] overflow-y-auto pr-2">
              {filteredGroups.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Filter className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">Nenhum grupo disponível</p>
                  {selectedDamId && (
                    <p className="text-xs mt-1">Tente outro filtro de barragem</p>
                  )}
                </div>
              ) : (
                filteredGroups.map((group) => {
                  const isSelected = selectedGroups.includes(group.id);
                  return (
                    <label
                      key={group.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleGroupToggle(group.id)}
                        className="mt-1 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-slate-900">{group.sigla}</p>
                        <p className="text-xs text-slate-600 mt-0.5">{group.damName}</p>
                        <Badge variant="secondary" className="text-xs mt-1.5">
                          {group.memberCount || 0} contato(s)
                      </Badge>
                    </div>
                  </label>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <MessageSquare className="h-5 w-5 text-blue-600" />
              Compor Mensagem
            </CardTitle>
            <p className="text-sm text-slate-600 mt-2">
              Personalize sua mensagem e escolha o tipo de envio
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSendMessages} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="templateSelect" className="text-sm font-medium">
                  Template *
                </Label>
                <Select value={selectedTemplateId} onValueChange={handleTemplateSelect} disabled={sending}>
                  <SelectTrigger id="templateSelect" className="h-11">
                    <SelectValue placeholder="Selecione um template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Tipo de Mensagem</Label>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant={messageType === MessageType.SMS ? 'default' : 'outline'}
                    onClick={() => setMessageType(MessageType.SMS)}
                    className="flex-1 h-11"
                    disabled={sending}
                  >
                    <Smartphone className="h-4 w-4 mr-2" />
                    SMS
                  </Button>
                  <Button
                    type="button"
                    variant={messageType === MessageType.WHATSAPP ? 'default' : 'outline'}
                    onClick={() => setMessageType(MessageType.WHATSAPP)}
                    className="flex-1 h-11"
                    disabled={sending}
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    WhatsApp
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="messageBody" className="text-sm font-medium">Mensagem *</Label>
                <textarea
                  id="messageBody"
                  value={messageBody}
                  onChange={() => undefined}
                  placeholder="Selecione um template para visualizar a mensagem"
                  rows={8}
                  maxLength={1600}
                  required
                  disabled={sending || !selectedTemplateId}
                  readOnly
                  className="w-full rounded-md border-2 border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-50"
                />
                <p className="text-xs text-slate-500">
                  A mensagem é preenchida pelo template selecionado e não pode ser editada nesta tela.
                </p>
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold"
                disabled={sending || selectedGroups.length === 0 || !messageBody.trim() || !selectedTemplateId}
              >
                {sending ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Enviando para {selectedGroups.length} grupo(s)...
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5 mr-2" />
                    Enviar para {totalRecipients} destinatário(s)
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {queueJob && (
        <Dialog
          open={isQueueModalOpen}
          onOpenChange={(open) => {
            if (queueIsRunning) {
              return;
            }
            setIsQueueModalOpen(open);
          }}
        >
          <DialogContent
            className="sm:max-w-3xl [&>button]:hidden"
            onInteractOutside={(event) => {
              if (queueIsRunning) {
                event.preventDefault();
              }
            }}
            onEscapeKeyDown={(event) => {
              if (queueIsRunning) {
                event.preventDefault();
              }
            }}
          >
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl">
                <Send className={`h-5 w-5 ${queueFinishedWithoutErrors ? 'text-emerald-600' : queueHasFailed ? 'text-red-600' : 'text-amber-600'}`} />
                {queueIsRunning ? 'Enviando mensagens em massa' : 'Resultado do envio em massa'}
              </DialogTitle>
            </DialogHeader>

            <div className={`rounded-lg border p-4 ${
              queueFinishedWithoutErrors
                ? 'bg-emerald-50 border-emerald-200'
                : queueHasFailed
                ? 'bg-red-50 border-red-200'
                : queueFinishedWithWarnings
                ? 'bg-amber-50 border-amber-200'
                : 'bg-slate-50 border-slate-200'
            }`}>
              <p className="text-sm text-slate-700">
                {queueJob.message || (queueIsRunning ? 'Processando envio em massa...' : 'Processamento finalizado.')}
              </p>
              <div className="w-full h-3 rounded-full bg-slate-200 overflow-hidden mt-3">
                <div
                  className={`h-3 transition-all duration-300 ${queueBarColor}`}
                  style={{ width: `${queueJob.progress || 0}%` }}
                />
              </div>

              <div className="flex flex-wrap gap-3 mt-3 text-sm text-slate-700">
                <span>{queueJob.processedItems || 0}/{queueJob.totalItems || 0} processado(s)</span>
                <span className="text-emerald-600 font-medium">{queueJob.successItems || 0} sucesso(s)</span>
                <span className="text-red-600 font-medium">{queueJob.failedItems || 0} falha(s)</span>
                <Badge variant={queueHasFailed ? 'destructive' : queueFinishedWithoutErrors ? 'default' : 'secondary'}>
                  {queueStatusLabel}
                </Badge>
              </div>
            </div>

            {sendLog.length > 0 && (() => {
              const totalRecipientsLog = sendLog.reduce((s, e) => s + (e.result?.recipientsCount ?? 0), 0);
              const totalSuccessLog = sendLog.reduce((s, e) => s + (e.result?.successCount ?? 0), 0);
              const totalFailedLog = sendLog.reduce((s, e) => s + (e.result?.failedCount ?? (e.error ? (e.result?.recipientsCount ?? 0) : 0)), 0);

              return (
                <div className="rounded-lg border border-slate-200 p-4 bg-white">
                  <div className="flex items-center gap-2 text-base font-semibold text-slate-900">
                    <ClipboardList className="h-5 w-5 text-blue-600" />
                    Log do envio
                  </div>
                  <div className="flex flex-wrap gap-3 mt-2 text-sm">
                    <span className="flex items-center gap-1 text-slate-600">
                      <Users className="h-4 w-4" /> {totalRecipientsLog} destinatário(s)
                    </span>
                    <span className="flex items-center gap-1 text-emerald-600 font-medium">
                      <CheckCircle2 className="h-4 w-4" /> {totalSuccessLog} enviado(s)
                    </span>
                    {totalFailedLog > 0 && (
                      <span className="flex items-center gap-1 text-red-600 font-medium">
                        <XCircle className="h-4 w-4" /> {totalFailedLog} falha(s)
                      </span>
                    )}
                  </div>

                  <div className="overflow-auto max-h-72 mt-3">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-white">
                        <tr className="border-b border-slate-200 text-slate-500 text-left">
                          <th className="pb-2 pr-4 font-medium">Grupo</th>
                          <th className="pb-2 pr-4 font-medium">Barragem</th>
                          <th className="pb-2 pr-4 font-medium text-right">Destinatários</th>
                          <th className="pb-2 pr-4 font-medium text-right">Enviados</th>
                          <th className="pb-2 font-medium text-right">Falhas</th>
                          <th className="pb-2 pl-4 font-medium text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sendLog.map((entry) => (
                          <tr key={entry.groupId} className="border-b border-slate-100 last:border-0">
                            <td className="py-2 pr-4 font-semibold text-slate-800">{entry.groupSigla}</td>
                            <td className="py-2 pr-4 text-slate-600">{entry.groupDamName}</td>
                            <td className="py-2 pr-4 text-right text-slate-700">{entry.result?.recipientsCount ?? '—'}</td>
                            <td className="py-2 pr-4 text-right text-emerald-700 font-medium">{entry.result?.successCount ?? '—'}</td>
                            <td className="py-2 text-right text-red-600 font-medium">
                              {entry.result?.failedCount !== undefined ? entry.result.failedCount : (entry.error ? entry.result?.recipientsCount ?? '—' : '—')}
                            </td>
                            <td className="py-2 pl-4 text-center">
                              {entry.error ? (
                                <Badge variant="destructive" className="text-xs">Falhou</Badge>
                              ) : entry.result?.status === 'SENT' || (entry.result && (entry.result.successCount ?? 0) > 0 && (entry.result.failedCount ?? 0) === 0) ? (
                                <Badge className="bg-emerald-600 text-xs">Enviado</Badge>
                              ) : entry.result?.status === 'FAILED' ? (
                                <Badge variant="destructive" className="text-xs">Falhou</Badge>
                              ) : (
                                <Badge variant="secondary" className="text-xs">Parcial</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}

            {!queueIsRunning && (
              <div className="flex justify-end">
                <Button onClick={() => setIsQueueModalOpen(false)}>
                  Fechar
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default SendMessagesPage;
